import { addDays } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import { Actions } from '../../store/actions/organisationActions';
import { AddButton } from '../../styledComponents/GlobalStyle';
import { convertToTitleCase } from '../utils/GlobalFunctions';
import Select from 'react-select';
import _ from 'lodash';
import { Divider, ToggleButton, ToggleButtonGroup, FormGroup, FormControlLabel, Grid, Switch, Tooltip } from '@mui/material';
import { TooltipText } from '../../styledComponents/GlobalStyle';
import { BetaTag } from '../Common/ReusableComponents/Reusable';

const today = new Date();

function Modal({
  showModal,
  toggleModal,
  organisationDelete,
  deleteOrganisationMetric,
  action,
  saveOrganisation,
  organisationItem,
  modalItem,
  disableUser,
  orgId,
  creationDate,
  reinviteUser,
  branding,
  facilitiesList,
  updateUserPermissions,
  deleteOrganisationHierarchy,
}) {
  const [loading, setLoading] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [buttonsTitle, setButtonsTitle] = useState({ confirm: '', cancel: '' });
  const [role, setRole] = useState([]);
  const [showToggles, setShowToggles] = useState(true);
  const emptyPermissions = {
    organisation: {
      view: false,
      edit: false,
      manage: false,
    },
    organisationMetrics: {
      view: false,
      create: false,
      edit: false,
      delete: false,
    },
    facilities: {
      '*': {
        view: false,
        edit: false,
        delete: false,
      },
    },
    facilityMetrics: {
      view: false,
      create: false,
      edit: false,
      delete: false,
    },
    reports: {
      view: false,
      create: false,
    },
    usageData: {
      view: false,
      create: false,
      edit: false,
      delete: false,
    },
    dashboard: {
      view: false,
      create: false,
      edit: false,
      delete: false,
    },
    developer: {
      read: false,
      write: false,
    },
  };
  const [permissions, setPermissions] = useState(null);
  const history = useHistory();
  const font = branding?.fonts?.[0]?.font?.body;
  const { btnTextColor, primaryButton, tooltipBackground, tooltipText, primaryColor, secondaryColor, textColor } =
    branding?.colors || {};

  useEffect(() => {
    if (action === 'manageUser') {
      if (modalItem?.permissions) {
        setPermissions(modalItem?.permissions);
      } else {
        setPermissions(emptyPermissions);
      }
    }
  }, [modalItem, action]);

  const handleClose = () => {
    toggleModal(false, null);
    if (action === 'addingOrg') {
      history.push('/organisations');
    }
  };

  const handleAction = async () => {
    setLoading(true);
    if (action === 'addingOrg') {
      await saveOrganisation(organisationItem, history);
      toggleModal(false, null);
    } else if (action === 'disableUser') {
      await disableUser(modalItem.sub || modalItem.email);
    } else if (action === 'reinviteUser') {
      await reinviteUser(modalItem);
    } else if (action === 'deleteOrgMetric') {
      deleteOrganisationMetric(organisationDelete.item.id);
    } else if (action === 'deleteOrgHierarchy') {
      deleteOrganisationHierarchy(organisationItem.id);
      console.log('deleteOrgHierarchy');
    } else if (action === 'orgMissingCompulsoryData') {
      history.push(`/organisations/${orgId}`);
      toggleModal(false, null);
    } else if (action === 'clientTestOrg') {
      toggleModal(false, null);
    } else if (action === 'manageUser') {
      await updateUserPermissions(modalItem.sub, permissions);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (action === 'addingOrg') {
      setModalTitle('Do you wish to add a new organisation?');
      setButtonsTitle({ confirm: 'Yes, I want to subscribe', cancel: 'No, I don’t want to subscribe' });
    } else if (action === 'disableUser') {
      setModalTitle('Are you sure you want to delete this user?');
      setButtonsTitle({ confirm: 'Yes, delete the user', cancel: 'Cancel' });
    } else if (action === 'reinviteUser') {
      setModalTitle('Are you sure you want to reinvite this user?');
      setButtonsTitle({ confirm: 'Yes, reinvite', cancel: 'Cancel' });
    } else if (action === 'deleteOrgMetric') {
      setModalTitle('Are you sure you wish to delete this organisation metric?');
      setButtonsTitle({ confirm: 'Yes, delete this data', cancel: 'Cancel' });
    } else if (action === 'deleteOrgHierarchy') {
      setModalTitle('Are you sure you wish to delete this child organisation?');
      setButtonsTitle({ confirm: 'Yes, delete this child', cancel: 'Cancel' });
    } else if (action === 'orgMissingCompulsoryData') {
      setModalTitle('Your organisation is missing some compulsory data');
      setButtonsTitle({ confirm: 'Go to Organisation Details', cancel: '' });
    } else if (action === 'clientTestOrg') {
      setModalTitle('You are about to set this organisation to a Client Test Organisation');
      setButtonsTitle({ confirm: 'Close', cancel: '' });
    } else if (action === 'manageUser') {
      setModalTitle(`${modalItem?.name} ${modalItem?.surname}'s Permissions`);
      setButtonsTitle({ confirm: 'Save Changes', cancel: 'Close' });
    }
  }, [action]);

  const facilityOptions = useMemo(() => {
    const overriddenIds = new Set(Object.keys(permissions?.facilities || {}));
    overriddenIds.delete('*');

    return facilitiesList
      .filter((facility) => !overriddenIds.has(facility.id))
      .map((facility) => ({ value: facility.id, label: facility.name }));
  }, [facilitiesList, permissions?.facilities]);

  const handleFacilityPermissionChange = (facilityId, key, checked) => {
    setPermissions((prev) => ({
      ...prev,
      facilities: {
        ...prev.facilities,
        [facilityId]: {
          ...prev.facilities[facilityId],
          [key]: checked,
        },
      },
    }));
  };

  const addFacilityOverride = (selectedOption) => {
    if (!selectedOption) return;

    setPermissions((prev) => ({
      ...prev,
      facilities: {
        ...prev.facilities,
        [selectedOption.value]: {
          ...prev.facilities['*'],
        },
      },
    }));
  };

  const removeFacilityOverride = (facilityId) => {
    setPermissions((prev) => {
      const newFacilities = { ...prev.facilities };
      delete newFacilities[facilityId];
      return {
        ...prev,
        facilities: newFacilities,
      };
    });
  };

  const facilityAdmin = {
    facilities: { '*': ['view', 'edit', 'delete'] },
    facilityMetrics: ['view', 'create', 'edit', 'delete'],
  };

  const reportsAdmin = { reports: ['view', 'create'] };

  const usageAdmin = {
    usageData: ['view', 'create', 'edit', 'delete'],
  };

  const developer = { developer: ['read', 'write'] };

  const organisationAdmin = {
    organisation: ['view', 'edit', 'manage'],
    organisationMetrics: ['view', 'create', 'edit', 'delete'],
    facilities: { '*': ['view', 'edit', 'delete'] },
    facilityMetrics: ['view', 'create', 'edit', 'delete'],
    reports: ['view', 'create'],
    usageData: ['view', 'create', 'edit', 'delete'],
    dashboard: ['view', 'create', 'edit', 'delete'],
    developer: ['read', 'write'],
  };

  const viewOnly = {
    organisation: ['view'],
    organisationMetrics: ['view'],
    facilities: { '*': ['view'] },
    facilityMetrics: ['view'],
    reports: ['view'],
    usageData: ['view'],
    dashboard: ['view'],
  };

  const generalUser = {
    organisation: ['view'],
    organisationMetrics: ['view'],
    facilities: { '*': ['view', 'edit', 'delete'] },
    facilityMetrics: ['view', 'create', 'edit', 'delete'],
    reports: ['view', 'create'],
    usageData: ['view', 'create', 'edit', 'delete'],
    dashboard: ['view', 'create', 'edit', 'delete'],
  };

  const checkPermissions = (requiredPermissions, permissions) => {
    return Object.entries(requiredPermissions).every(([key, values]) => {
      if (Array.isArray(values)) {
        // Check if all required values exist and are true in permissions
        return values.every((value) => permissions[key]?.[value] === true);
      } else if (typeof values === 'object') {
        // Recursively check nested objects
        return checkPermissions(values, permissions[key] || {});
      }
      return false;
    });
  };

  useMemo(() => {
    if (action === 'manageUser') {
      // Check if the user has the required permissions, if yes turn role on, if not turn it off
      if (checkPermissions(viewOnly, permissions)) {
        setRole((prevState) => Array.from(new Set([...prevState, 'View Only'])));
      } else {
        setRole((prevState) => prevState.filter((role) => role !== 'View Only'));
      }
      if (checkPermissions(facilityAdmin, permissions)) {
        setRole((prevState) => Array.from(new Set([...prevState, 'Facility Admin'])));
      } else {
        setRole((prevState) => prevState.filter((role) => role !== 'Facility Admin'));
      }
      if (checkPermissions(reportsAdmin, permissions)) {
        setRole((prevState) => Array.from(new Set([...prevState, 'Reports Admin'])));
      } else {
        setRole((prevState) => prevState.filter((role) => role !== 'Reports Admin'));
      }
      if (checkPermissions(usageAdmin, permissions)) {
        setRole((prevState) => Array.from(new Set([...prevState, 'Usage Admin'])));
      } else {
        setRole((prevState) => prevState.filter((role) => role !== 'Usage Admin'));
      }
      if (checkPermissions(organisationAdmin, permissions)) {
        setRole((prevState) => Array.from(new Set([...prevState, 'Organisation Admin'])));
      } else {
        setRole((prevState) => prevState.filter((role) => role !== 'Organisation Admin'));
      }
      if (checkPermissions(developer, permissions)) {
        setRole((prevState) => Array.from(new Set([...prevState, 'Developer'])));
      } else {
        setRole((prevState) => prevState.filter((role) => role !== 'Developer'));
      }
      if (checkPermissions(generalUser, permissions)) {
        setRole((prevState) => Array.from(new Set([...prevState, 'General User'])));
      } else {
        setRole((prevState) => prevState.filter((role) => role !== 'General User'));
      }
    }
    // eslint-disable-next-line
  }, [permissions]);

  const roleMappings = {
    'Organisation Admin': organisationAdmin,
    'Facility Admin': facilityAdmin,
    'Reports Admin': reportsAdmin,
    'Usage Admin': usageAdmin,
    'View Only': viewOnly,
    Developer: developer,
    'General User': generalUser,
  };

  let generalUserStyle = { color: 'black', backgroundColor: '#DDD' };

  const handleRoleChange = (event, newRole) => {
    const prevRole = role;
    setRole(newRole);

    if (newRole?.includes('General User')) {
      setPermissions(emptyPermissions);
      setRole([]);
    }

    // Function to recursively update permissions (enable or disable based on setValue)
    const updatePermissions = (permissionsStructure, permissionsObject, setValue) => {
      Object.entries(permissionsStructure).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // If it's an array, convert it into an object with the permissions as keys and set their values to true or false
          permissionsObject[key] = permissionsObject[key] || {}; // Create the object if it doesn't exist
          value.forEach((perm) => {
            permissionsObject[key][perm] = setValue; // Set permission to true or false
          });
        } else if (typeof value === 'boolean') {
          // If it's a boolean value, update it directly (for single permissions)
          permissionsObject[key] = setValue;
        } else if (typeof value === 'object') {
          // If it's a nested object, recursively update its permissions
          permissionsObject[key] = permissionsObject[key] || {}; // Create nested object if not exists
          updatePermissions(value, permissionsObject[key], setValue); // Recurse for nested permissions
        }
      });
    };

    if (newRole.includes('General User')) {
      // 1) reset everything
      const newEditedState = _.cloneDeep(emptyPermissions);

      // 2) then apply only the General User permissions
      if (roleMappings['General User']) {
        updatePermissions(roleMappings['General User'], newEditedState, true);
      }

      // 3) if you also want to apply any _other_ roles alongside GU, do so here:
      updatePermissions(roleMappings['General User'], newEditedState, true);

      setPermissions(newEditedState);
      return;
    } else if (newRole.includes('Organisation Admin')) {
      // 1) reset everything
      const newEditedState = _.cloneDeep(emptyPermissions);

      // 2) then apply only the Organisation Admin permissions
      if (roleMappings['Organisation Admin']) {
        updatePermissions(roleMappings['Organisation Admin'], newEditedState, true);
      }

      // 3) if you also want to apply any _other_ roles alongside GU, do so here:
      updatePermissions(roleMappings['Organisation Admin'], newEditedState, true);

      setPermissions(newEditedState);
      return;
    }
  };

  const handleOtherRoleChange = (event, newRole) => {
    const prevRole = role;
    setRole(newRole);

    // Function to recursively update permissions (enable or disable based on setValue)
    const updatePermissions = (permissionsStructure, permissionsObject, setValue) => {
      Object.entries(permissionsStructure).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // If it's an array, convert it into an object with the permissions as keys and set their values to true or false
          permissionsObject[key] = permissionsObject[key] || {}; // Create the object if it doesn't exist
          value.forEach((perm) => {
            permissionsObject[key][perm] = setValue; // Set permission to true or false
          });
        } else if (typeof value === 'boolean') {
          // If it's a boolean value, update it directly (for single permissions)
          permissionsObject[key] = setValue;
        } else if (typeof value === 'object') {
          // If it's a nested object, recursively update its permissions
          permissionsObject[key] = permissionsObject[key] || {}; // Create nested object if not exists
          updatePermissions(value, permissionsObject[key], setValue); // Recurse for nested permissions
        }
      });
    };

    // Clone the current permissions to avoid direct mutation
    const newEditedState = _.cloneDeep(permissions);

    // Loop through each selected role and update permissions
    newRole.forEach((selectedRole) => {
      if (roleMappings[selectedRole]) {
        updatePermissions(roleMappings[selectedRole], newEditedState, true); // Enable permissions for selected roles
      }
    });

    // Ensure facilities.*.view is true if Usage Admin is selected
    if (newRole.includes('Usage Admin')) {
      newEditedState.facilities['*'].view = true;
    } else if (prevRole.includes('Usage Admin') && !newRole.includes('Usage Admin') && !newRole.includes('Facility Admin')) {
      // If Usage Admin is removed and facilities role is not selected, ensure facilities.*.view is set to false
      newEditedState.facilities['*'].view = false; // Disable view permission for facilities
    }

    // Handle role removals: check for permissions that should be turned off
    if (prevRole.length > 0) {
      prevRole.forEach((selectedRole) => {
        if (!newRole.includes(selectedRole) && roleMappings[selectedRole]) {
          // Disable permissions for removed roles
          updatePermissions(roleMappings[selectedRole], newEditedState, false); // Disable permissions for the removed role
        }
      });
    }

    // Update the permissions state with the new edited state
    setPermissions(newEditedState);
  };

  const expireDay = useMemo(() => {
    if (action === 'clientTestOrg') {
      if (!creationDate) {
        return addDays(new Date(), 30);
      } else {
        return addDays(new Date(creationDate), 30);
      }
    } else {
      return null;
    }
  }, [action, creationDate]);

  // Reusable component for sections with switches
  const Section = ({ title }) => (
    <Grid item xs={5}>
      <FormGroup style={{ marginTop: '10px' }}>
        {permissions &&
          Object.entries(permissions?.[title])
            ?.sort(([keyA], [keyB]) => switchOrder.indexOf(keyA) - switchOrder.indexOf(keyB))
            ?.map(([key, value], index) => (
              <FormControlLabel
                key={index}
                style={{ margin: 0 }}
                control={
                  <Switch
                    id={`${title}-${key}`}
                    onChange={(e, checked) => setPermissions(prev => 
                      title === 'organisation' && key === 'manage'
                      ? { ...prev, [title]: { ...prev[title], [key]: checked }, ...(checked && { facilities: { '*': prev.facilities['*'] } })}
                      : { ...prev, [title]: { ...prev[title], [key]: checked } })}
                    checked={value}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: primaryButton, // Color for the circle when switched on
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: primaryButton, // Color for the track when switched on
                      },
                    }}
                  />
                }
                label={convertToTitleCase(key)}
              />
            ))}
      </FormGroup>
    </Grid>
  );

  const facilityStyle = permissions?.organisation?.manage
    ? {
        zIndex: 100,
        width: '100%',
        backgroundColor: '#BBB',
        height: '100%',
        opacity: 0.52,
        padding: 10,
        pointerEvents: 'none',
        borderRadius: '10px',
      }
    : { backgroundColor: 'transparent' };

  const FacilitySection = ({ title }) => (
    <>
      <div style={facilityStyle}>
        <Grid container spacing={2}>
          <Grid item xs={7}>
            <>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <b style={{ textAlign: 'right' }}>{convertToTitleCase(title)}</b>{' '}
                <p style={{ textAlign: 'right' }}>{descriptions(title)}</p>
                {facilityOptions?.length > 0 && (
                  <div style={{ width: '100%' }}>
                    <Select options={facilityOptions} onChange={addFacilityOverride} placeholder='Add Facility Override' />
                  </div>
                )}
              </div>
            </>
          </Grid>
          <Grid item xs={5}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div
                className='badge'
                style={{ marginLeft: '0.5rem', alignSelf: 'flex-start', backgroundColor: primaryColor, color: 'white' }}>
                Default Permissions
              </div>
              <FormGroup>
                {Object.entries(permissions?.['facilities']?.['*'] || {})
                  ?.sort(([keyA], [keyB]) => switchOrder.indexOf(keyA) - switchOrder.indexOf(keyB))
                  ?.map(([key, value]) => (
                    <FormControlLabel
                      key={key}
                      style={{ margin: 0 }}
                      control={
                        <Switch
                          id={`facilities-*-${key}`}
                          onChange={(e) => {
                            handleFacilityPermissionChange('*', key, e.target.checked);
                          }}
                          checked={value}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: primaryButton, // Color for the circle when switched on
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: primaryButton, // Color for the track when switched on
                            },
                          }}
                        />
                      }
                      label={convertToTitleCase(key)}
                    />
                  ))}
              </FormGroup>
            </div>
          </Grid>
        </Grid>
      </div>
    </>
  );

  const descriptions = (key) => {
    switch (key) {
      case 'organisation':
        return 'View: Allow user to view organisation details\n Edit: Allow user to modify organisation details\n Manage: Grants full access. Allow user to create facilities, add new users and manage permissions, subscription, and group reporting\n';
      case 'organisationMetrics':
        return 'View: Allow user to view organisation metrics\n Create: Allow user to create organisation metrics\n Edit: Allow user to modify organisation metics\n Delete: Allow user to delete organisation metrics\n';
      case 'reports':
        return 'View: Allow user to access and read reports\n Create: Allow user to generate reports\n';
      case 'usageData':
        return 'View: Allow user to view usage data\n Create: Allow user to create or duplicate usage data\n Edit: Allow user to modify usage data\n Delete: Allow user to delete usage data\n';
      case 'facilityMetrics':
        return 'View: Allow user to view facility metrics\n Create: Allow user to create facility metrics\n Edit: Allow user to modify facility metics\n Delete: Allow user to remove facility metrics\n';
      case 'dashboard':
        return 'View: Allow user to view dashboard\n Create: Allow user to add dashboard elements\n Edit: Allow user to update dashboard elements\n Delete: Allow user to remove dashboard elements\n';
      case 'developer':
        return 'Read: Enables read access of data from API endpoints This includes usage data, metrics, reports etc\n Write: Allow data modification (create/edit/delete) through API endpoints\n';
      case 'facilities':
        return 'Default permissions apply to all facilities unless overridden by individual facility permissions (see below dropdown)\n';
    }
  };

  const sortOrder = [
    'organisation',
    'organisationMetrics',
    'facilities',
    'reports',
    'usageData',
    'dashboard',
    'developer',
    'facilityMetrics',
  ];

  const switchOrder = ['view', 'create', 'edit', 'delete', 'manage', 'read', 'write'];

  const ModelContent = () => {
    if (action === 'addingOrg') {
      return (
        <p>
          By adding a new organisation to your account, you'll need to subscribe the added organisation to have access to our
          application.
        </p>
      );
    } else if (action === 'disableUser') {
      return <p>By confirming you are going to delete this user account.</p>;
    } else if (action === 'reinviteUser') {
      return <p>By confirming you are going to reinvite this user.</p>;
    } else if (action === 'deleteOrgMetric') {
      return <p>This action cannot be undone.</p>;
    } else if (action === 'deleteOrgHierarchy') {
      return <p>This action cannot be undone.</p>;
    } else if (action === 'orgMissingCompulsoryData') {
      return (
        <p>
          Your Organisation has some missing compulsory Data. <br /> To keep using the application please go to your
          organisation details page and fill the compuslory fields missing.
        </p>
      );
    } else if (action === 'clientTestOrg') {
      return (
        <p>
          Please note, by selecting this option, this organisation will only be retained for{' '}
          <b>30 days from the date it was created</b>. The organisation and all associated test data will be{' '}
          <b>
            permanently deleted at 11pm{' '}
            {expireDay > today ? (
              `on ${expireDay?.toLocaleDateString('en-GB')}`
            ) : (
              <span style={{ color: 'red' }}>TODAY</span>
            )}
          </b>
          . You can check how long the organisation has remaining by looking in the bottom left of the dashboard page (with
          the organisation selected).
        </p>
      );
    } else if (action === 'manageUser') {
      return (
        <div style={{ display: 'flex', marginBottom: 20, flexDirection: 'column', fontSize: '13px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'row',
              flexWrap: 'wrap',
            }}>
            <Tooltip
              title={
                <TooltipText>
                  {'Selecting a role will automatically toggle on/off the respective permissions associated with that role.'}
                </TooltipText>
              }
              placement='right'
              arrow
              componentsProps={{
                tooltip: { sx: { backgroundColor: tooltipBackground, color: tooltipText } },
                arrow: { sx: { color: tooltipBackground } },
              }}>
              <b style={{ marginRight: 5, marginBottom: 5 }}>Preset Roles:</b>
              <i className='fas fa-info-circle' style={{ cursor: 'pointer', color: primaryColor, fontSize: 14 }} />
            </Tooltip>
            <button
              id='reset-permissions'
              style={{
                backgroundColor: 'transparent',
                border: `2px ${primaryButton} solid`,
                marginLeft: '10px',
                borderRadius: '5px',
                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
              }}
              onClick={() => {
                setPermissions(emptyPermissions);
                setRole([]);
              }}>
              Reset
            </button>
            <div>
              <ToggleButtonGroup
                value={role}
                exclusive
                onChange={handleRoleChange}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  marginTop: '10px',
                  justifyContent: 'space-evenly',
                  '& .MuiToggleButton-root': {
                    color: 'black', // Default text color
                    backgroundColor: '#DDD', // Default background color
                    fontFamily: font,
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    borderColor: '#AAA',
                    '&.Mui-selected': {
                      color: btnTextColor, // Text color when selected
                      backgroundColor: primaryButton, // Background color when selected
                    },
                    '&:hover': {
                      backgroundColor: 'none',
                    },
                    '&:focus': {
                      color: btnTextColor,
                      backgroundColor: primaryButton,
                    },
                  },
                }}>
                <Tooltip
                  title={
                    <TooltipText>
                      {
                        'Full administrative control over the organisation. Can manage users, billing, facility creation, and group reporting in addition to accessing all information across the platform.'
                      }
                    </TooltipText>
                  }
                  placement='bottom'
                  arrow
                  componentsProps={{
                    tooltip: { sx: { backgroundColor: tooltipBackground, color: tooltipText } },
                    arrow: { sx: { color: tooltipBackground } },
                  }}>
                  <ToggleButton
                    id='OrganisationAdmin-toggle'
                    disableRipple
                    value='Organisation Admin'
                    style={{ borderRadius: '5px' }}>
                    Organisation Manager
                  </ToggleButton>
                </Tooltip>
                <Tooltip
                  title={
                    <TooltipText>
                      {
                        'Standard user with access to core platform features. Can modify usage data, facility information, and generate reports.  View only access for organisation details and metrics.'
                      }
                    </TooltipText>
                  }
                  placement='bottom'
                  arrow
                  componentsProps={{
                    tooltip: { sx: { backgroundColor: tooltipBackground, color: tooltipText } },
                    arrow: { sx: { color: tooltipBackground } },
                  }}>
                  <ToggleButton
                    id='GeneralUser-toggle'
                    disableRipple
                    value='General User'
                    style={{
                      borderRadius: '5px',
                      border: '1px solid #AAA',
                      ...(role?.includes('Organisation Admin') ? generalUserStyle : {}),
                    }}>
                    General User
                  </ToggleButton>
                </Tooltip>
              </ToggleButtonGroup>

              <div style={{ display: 'flex' }}>
                <hr style={{ color: 'black', width: '100%' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <b>Advanced Access Options: </b>
              </div>
              <ToggleButtonGroup
                value={role}
                onChange={handleOtherRoleChange}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  marginTop: '10px',
                  gap: '15px',
                  justifyContent: 'space-evenly',
                  '& .MuiToggleButton-root': {
                    color: 'black', // Default text color
                    backgroundColor: '#DDD', // Default background color
                    fontFamily: font,
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    borderColor: '#AAA',
                    '&.Mui-selected': {
                      color: btnTextColor, // Text color when selected
                      backgroundColor: primaryButton, // Background color when selected
                    },
                    '&:hover': {
                      backgroundColor: 'none',
                    },
                    '&:focus': {
                      color: btnTextColor,
                      backgroundColor: primaryButton,
                    },
                  },
                }}>
                <Tooltip
                  title={
                    <TooltipText>
                      {
                        'Access to facility-related information. Can create, edit, and delete facilities and associated facility metrics.'
                      }
                    </TooltipText>
                  }
                  placement='bottom'
                  arrow
                  componentsProps={{
                    tooltip: { sx: { backgroundColor: tooltipBackground, color: tooltipText } },
                    arrow: { sx: { color: tooltipBackground } },
                  }}>
                  <ToggleButton
                    id='FacilityAdmin-toggle'
                    disableRipple
                    value='Facility Admin'
                    style={{ borderRadius: '5px', border: '1px solid #AAA' }}>
                    Facility Access
                  </ToggleButton>
                </Tooltip>
                <Tooltip
                  title={<TooltipText>{'Can view existing reports and generate new ones.'}</TooltipText>}
                  placement='bottom'
                  arrow
                  componentsProps={{
                    tooltip: { sx: { backgroundColor: tooltipBackground, color: tooltipText } },
                    arrow: { sx: { color: tooltipBackground } },
                  }}>
                  <ToggleButton
                    id='ReportsAdmin-toggle'
                    disableRipple
                    value='Reports Admin'
                    style={{ borderRadius: '5px', border: '1px solid #AAA' }}>
                    Reports Access
                  </ToggleButton>
                </Tooltip>
                <Tooltip
                  title={
                    <TooltipText>
                      {'Can access the usage data table with permissions to create, edit, and delete usage records.'}
                    </TooltipText>
                  }
                  placement='bottom'
                  arrow
                  componentsProps={{
                    tooltip: { sx: { backgroundColor: tooltipBackground, color: tooltipText } },
                    arrow: { sx: { color: tooltipBackground } },
                  }}>
                  <ToggleButton
                    id='UsageAdmin-toggle'
                    disableRipple
                    value='Usage Admin'
                    style={{ borderRadius: '5px', border: '1px solid #AAA' }}>
                    Usage Data Access
                  </ToggleButton>
                </Tooltip>
                <Tooltip
                  title={
                    <TooltipText>
                      {
                        'View only access to the platform. Can view the organisation and facility settings, as well as usage data and reports, but cannot make any changes.'
                      }
                    </TooltipText>
                  }
                  placement='bottom'
                  arrow
                  componentsProps={{
                    tooltip: { sx: { backgroundColor: tooltipBackground, color: tooltipText } },
                    arrow: { sx: { color: tooltipBackground } },
                  }}>
                  <ToggleButton
                    id='ViewOnly-toggle'
                    disableRipple
                    value='View Only'
                    style={{ borderRadius: '5px', border: '1px solid #AAA' }}>
                    View All
                  </ToggleButton>
                </Tooltip>
                <Tooltip
                  title={
                    <TooltipText>
                      {'Access to developer tools. Intended for technical users who manage custom integrations.'}
                    </TooltipText>
                  }
                  placement='bottom'
                  arrow
                  componentsProps={{
                    tooltip: { sx: { backgroundColor: tooltipBackground, color: tooltipText } },
                    arrow: { sx: { color: tooltipBackground } },
                  }}>
                  <ToggleButton
                    id='Developer-toggle'
                    disableRipple
                    value='Developer'
                    style={{ borderRadius: '5px', border: '1px solid #AAA' }}>
                    Developer
                  </ToggleButton>
                </Tooltip>
              </ToggleButtonGroup>
            </div>
          </div>
          <div style={{ display: 'flex', marginTop: 20 }}>
            <Divider style={{ color: 'black', width: '100%' }} flexItem />
          </div>
          <div style={{ display: 'flex', marginTop: 20, justifyContent: 'center', alignItems: 'center' }}>
            <b style={{ cursor: 'pointer' }} onClick={() => setShowToggles(!showToggles)}>
              Configure Permissions:{' '}
              {showToggles ? <i className='fas fa-chevron-up' /> : <i className='fas fa-chevron-down' />}
            </b>
          </div>
          <div
            style={{
              display: showToggles ? 'flex' : 'none',
              marginTop: 20,
              flexDirection: 'column',
              gap: 10,
              flexWrap: 'wrap',
            }}>
            {permissions &&
              Object.entries(permissions)
                ?.sort(([keyA], [keyB]) => sortOrder.indexOf(keyA) - sortOrder.indexOf(keyB))
                ?.map(([key, value]) => {
                  if (key !== 'facilities') {
                    return (
                      <>
                        <Grid container key={key} spacing={0}>
                          <Grid item xs={7}>
                            <div style={{ width: '100%' }}>
                              <div style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                {key == 'dashboard' ? (
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'flex-end',
                                    }}>
                                    {convertToTitleCase(key)}
                                    <BetaTag backgroundColor={textColor} color={'#fff'} />
                                  </div>
                                ) : (
                                  convertToTitleCase(key)
                                )}
                              </div>
                              {descriptions(key)
                                ?.split('\n')
                                .filter(Boolean)
                                .map((desc, idx) => (
                                  <p
                                    key={idx}
                                    style={{ textAlign: 'right', margin: 0, textWrap: 'pretty', marginBottom: '20px' }}>
                                    {desc}
                                  </p>
                                ))}
                            </div>
                          </Grid>
                          <Section title={key} checkboxes={value}></Section>
                          {/* <Grid item xs={1}></Grid> */}
                        </Grid>
                      </>
                    );
                  }
                })}

            <FacilitySection title='facilities' />
            <div>
              {permissions?.facilities &&
                Object.entries(permissions?.['facilities'] || {})
                  .filter(([id]) => id !== '*')
                  .map(([facilityId, facilityPermissions]) => {
                    const facility = facilitiesList.find((f) => f.id === facilityId);
                    if (!facility) return null;

                    return (
                      <Grid container spacing={1} key={facilityId}>
                        <Grid item xs={6}>
                          <div style={{ width: '100%' }}>
                            <div style={{ textAlign: 'right', fontWeight: 'bold' }}>{'Facility'}</div>
                          </div>
                        </Grid>
                        <Grid item xs={6}>
                          <div key={facilityId} className='border p-3 rounded' style={{ marginBottom: '5px' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <div
                                className='badge me-2'
                                style={{
                                  marginLeft: '0.5rem',
                                  alignSelf: 'flex-start',
                                  flexWrap: 'wrap',
                                  backgroundColor: secondaryColor,
                                  color: 'white',
                                  textOverflow: 'ellipsis',
                                }}>
                                <p style={{ textOverflow: 'ellipsis', margin: 0, whiteSpace: 'normal' }}>{facility.name}</p>
                              </div>
                              <button
                                className='btn btn-link btn-sm'
                                style={{ padding: '0 10px' }}
                                title='Delete Overwrite'
                                onClick={() => removeFacilityOverride(facilityId)}>
                                <i className='fas fa-xmark' style={{ fontSize: '14px' }} />
                              </button>
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                justifyContent: 'center',
                                flexDirection: 'column',
                              }}>
                              {Object.entries(facilityPermissions || {})
                                ?.sort(([keyA], [keyB]) => switchOrder.indexOf(keyA) - switchOrder.indexOf(keyB))
                                ?.map(([key, value]) => (
                                  <FormControlLabel
                                    style={{ margin: 0 }}
                                    key={key}
                                    control={
                                      <Switch
                                        sx={{
                                          '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: primaryButton, // Color for the circle when switched on
                                          },
                                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: primaryButton, // Color for the track when switched on
                                          },
                                        }}
                                        checked={value}
                                        onChange={(e) => handleFacilityPermissionChange(facilityId, key, e.target.checked)}
                                      />
                                    }
                                    label={<span className='text-capitalize'>{key}</span>}
                                  />
                                ))}
                            </div>
                          </div>
                        </Grid>
                      </Grid>
                    );
                  })}
            </div>
          </div>
        </div>
      );
    }
  };

  if (!showModal) return null;

  return (
    <div className='modal' style={{ display: 'block', background: 'rgba(0,0,0,.5)' }}>
      <div className='modal-dialog' style={{ height: '100vh', maxHeight: '90vh' }}>
        <div className='modal-content'>
          <div className='modal-header' style={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
            <h4 className='modal-title'>{modalTitle}</h4>
            <button onClick={handleClose} className='modal-close'>
              <i className='fa fa-times' />
            </button>
          </div>
          <div
            className='modal-body'
            style={{
              maxHeight: '75vh',
              overflow: 'auto',
              borderBottomRightRadius: '6px',
              borderBottomLeftRadius: '6px',
            }}>
            <ModelContent />
          </div>
          <div className='modal-footer' style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
            <span
              style={{
                color: 'red',
                marginBottom: 10,
                textAlign: 'center',
                display: action === 'manageUser' && _.isEqual(permissions, emptyPermissions) ? 'block' : 'none',
              }}>
              <i className='fa fa-info-circle' /> The current user has no permissions and will not be able to use the
              application
            </span>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <AddButton
                onClick={handleAction}
                className='btn btn-danger'
                id='Organisation-modal-confirm'
                disabled={loading || (action === 'manageUser' && _.isEqual(permissions, modalItem?.permissions))}
                style={{ marginRight: 10, display: !buttonsTitle.confirm && 'none' }}>
                {loading && <i className='fa fa-spin fa-spinner' style={{ marginRight: 5 }} />}
                {buttonsTitle?.confirm}
              </AddButton>
              <button
                onClick={handleClose}
                className='btn btn-secondary'
                id='Organisation-modal-cancel'
                style={{ display: !buttonsTitle.cancel && 'none', border: '1px solid #ccc' }}>
                {buttonsTitle?.cancel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  const { status: showModal, item: modalItem, action } = state.organisation?.modal || {};
  const { hasSubscriptionActive, id: orgId, creationDate } = state.organisation?.details || {};

  return {
    showModal,
    modalItem,
    organisationDelete: state.organisation?.modal,
    action,
    organisationItem: state.organisation?.modal?.item,
    hasSubscriptionActive,
    orgId,
    creationDate,
    branding: state?.branding || {},
    facilitiesList: state?.facility?.list || [],
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    toggleModal: (status, item) => dispatch(Actions.toggleModal(status, item)),
    disableUser: (item) => dispatch(Actions.disableUser(item)),
    reinviteUser: (item) => dispatch(Actions.reinviteUser(item)),
    deleteOrganisationMetric: (id) => dispatch(Actions.deleteOrganisationMetric(id)),
    saveOrganisation: (organisation, history) => dispatch(Actions.saveOrganisation(organisation, history)),
    updateUserPermissions: (cognitoId, permissions) => dispatch(Actions.updateUserPermissions(cognitoId, permissions)),
    deleteOrganisationHierarchy: (id) => dispatch(Actions.deleteOrganisationHierarchy(id)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Modal);
